/**
 * Calculates and formats the time elapsed since the user's last activity
 *
 * @param lastActive - The timestamp of last activity. Accepts:
 *   - Date object: `new Date('2024-01-15T10:30:00Z')`
 *   - ISO string: `'2024-01-15T10:30:00Z'` or `'2024-01-15'`
 *   - Unix timestamp (ms): `1705315800000`
 *   - Unix timestamp (s): `1705315800` (automatically detected)
 *
 * @returns Human-readable time elapsed format:
 *   - 0–59 seconds → "just now"
 *   - 1–59 minutes → "X minutes ago"
 *   - 1–23 hours → "X hours ago"
 *   - 1–6 days → "X days ago"
 *   - 7–30 days → "X weeks ago"
 *   - 31–364 days → "X months ago"
 *   - 365+ days → "X years ago"
 */
import { t } from "i18next";
export const getTimeElapsed = (lastActive) => {
    let normalizedInput = lastActive;
    // Handle Unix timestamp in seconds (convert to milliseconds)
    if (typeof lastActive === "number" && lastActive < 1e12) {
        normalizedInput = lastActive * 1000;
    }
    const lastActiveDate = new Date(normalizedInput);
    const now = new Date();
    // Validate the date
    if (isNaN(lastActiveDate.getTime())) {
        return t("common:elapsedTime.justNow");
    }
    const diffMs = now.getTime() - lastActiveDate.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    // Handle negative differences and 0-59 seconds
    if (diffSeconds < 60) {
        return t("common:elapsedTime.justNow");
    }
    // 1-59 minutes
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) {
        return t("common:elapsedTime.minute", { count: diffMinutes });
    }
    // 1-23 hours
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
        return t("common:elapsedTime.hour", { count: diffHours });
    }
    // 1-6 days
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays <= 6) {
        return t("common:elapsedTime.day", { count: diffDays });
    }
    // 7-30 days (weeks)
    if (diffDays <= 30) {
        const weeks = Math.floor(diffDays / 7);
        return t("common:elapsedTime.week", { count: weeks });
    }
    // 31-364 days (months)
    if (diffDays < 365) {
        // More accurate month calculation
        const months = Math.floor(diffDays / 30.44);
        return t("common:elapsedTime.month", { count: months });
    }
    // 365+ days (years)
    const years = Math.floor(diffDays / 365);
    return t("common:elapsedTime.year", { count: years });
};
